"use client";

import { useRef } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { countTo, reduceMotion } from "@/lib/motion";
import { FACTS } from "@/content/quikit";

/** "Quikit at a glance" — four numbers that count up as the strip enters. */
export function Facts() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const items = gsap.utils.toArray<HTMLElement>("[data-fact]", el);
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: "top 82%", toggleActions: PLAY_ONCE } });
      tl.fromTo(items, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.2, ease: "silk", stagger: 0.1 }).fromTo(
        gsap.utils.toArray("[data-fact-rule]", el),
        { scaleX: 0 },
        { scaleX: 1, duration: 1.3, ease: "silk", stagger: 0.1 },
        0.1,
      );
      items.forEach((item, i) => {
        const f = FACTS[i];
        tl.add(countTo(item.querySelector("[data-fact-value]"), f.value, { prefix: f.prefix, duration: 1.6 }), 0.15 + i * 0.1);
      });
    },
    { scope: root },
  );

  return (
    <section id="facts" ref={root} aria-label="Quikit at a glance" className="pt-[clamp(90px,10vw,150px)]">
      <div className="wrap grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
        {FACTS.map((f) => (
          <div key={f.label} data-fact="" data-reveal-init="">
            <span data-fact-rule="" className="block h-px origin-left bg-ink/15" />
            <p className="mt-5 text-[clamp(2.4rem,4vw,3.6rem)] font-[450] leading-none tracking-[-0.045em]">
              <span data-fact-value="" className="tabular-nums">
                {f.prefix}
                {f.value}
              </span>
              {/* Unit sits on its own line beneath the number (e.g. "14" / "days") */}
              {f.suffix && (
                <span className="mt-1.5 block text-[0.42em] leading-none tracking-[-0.03em] text-ink/70">
                  {f.suffix.trim()}
                </span>
              )}
            </p>
            <p className="mt-3 max-w-[18ch] text-[14px] leading-[1.5] text-muted">{f.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
